/**
 * Room legality.
 *
 * Two jobs, deliberately in one place:
 *  1. `parseRoom` — never trust a room. Rooms arrive from share links, from
 *     localStorage written by an older build, and later from other players via
 *     the server. Anything malformed must be rejected here, not crash the sim.
 *  2. `checkRoom` — the *design* rules: budget, caps, and above all that the
 *     chest is actually reachable. A room that cannot be beaten isn't a
 *     prank, it's a wall, and nobody shares a wall.
 *
 * Warnings are separate from errors on purpose: they are how the builder
 * teaches bluffing without forbidding anything.
 */

import type { PropInstance, RoomDef, Rot } from './room'
import {
  ROOM_CONFIG,
  ROOM_VERSION,
  cellDistance,
  cellKey,
  countByType,
  sameCell,
  spentBudget,
} from './room'
import { isPropTypeId, propType } from './props'

export interface Issue {
  code: string
  message: string
  /** Instance id when the issue belongs to one prop. */
  iid?: number
}

export interface CheckResult {
  ok: boolean
  errors: Issue[]
  warnings: Issue[]
  spent: number
  budget: number
}

function isFiniteInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && Number.isInteger(v)
}

function clampString(v: unknown, max: number, fallback: string): string {
  if (typeof v !== 'string') return fallback
  const trimmed = v.replace(/\s+/g, ' ').trim()
  return trimmed.length > max ? trimmed.slice(0, max) : trimmed
}

/**
 * Structural parse. Returns null when the payload cannot be salvaged.
 * Out-of-range props are dropped rather than failing the whole room — a share
 * link from an older build should still be playable.
 */
export function parseRoom(input: unknown): RoomDef | null {
  if (!input || typeof input !== 'object') return null
  const raw = input as Record<string, unknown>
  if (raw.v !== ROOM_VERSION) return null

  const width = isFiniteInt(raw.width) ? raw.width : ROOM_CONFIG.width
  const depth = isFiniteInt(raw.depth) ? raw.depth : ROOM_CONFIG.depth
  if (width < 6 || width > 24 || depth < 6 || depth > 24) return null

  const doorRaw = raw.door as Record<string, unknown> | undefined
  const chestRaw = raw.chest as Record<string, unknown> | undefined
  if (!doorRaw || !chestRaw) return null
  if (!isFiniteInt(doorRaw.x) || !isFiniteInt(doorRaw.z)) return null
  if (!isFiniteInt(chestRaw.x) || !isFiniteInt(chestRaw.z)) return null

  const inBounds = (x: number, z: number) => x >= 0 && x < width && z >= 0 && z < depth
  if (!inBounds(doorRaw.x, doorRaw.z) || !inBounds(chestRaw.x, chestRaw.z)) return null

  const props: PropInstance[] = []
  const seenCells = new Set<number>()
  if (Array.isArray(raw.props)) {
    for (const item of raw.props.slice(0, ROOM_CONFIG.maxProps * 2)) {
      if (!item || typeof item !== 'object') continue
      const p = item as Record<string, unknown>
      if (!isPropTypeId(p.type)) continue
      if (!isFiniteInt(p.x) || !isFiniteInt(p.z)) continue
      if (!inBounds(p.x, p.z)) continue
      const key = cellKey(p.x, p.z)
      if (seenCells.has(key)) continue
      seenCells.add(key)
      const rot = isFiniteInt(p.rot) ? (((p.rot % 4) + 4) % 4) : 0
      props.push({
        iid: isFiniteInt(p.iid) ? p.iid : props.length + 1,
        type: p.type,
        x: p.x,
        z: p.z,
        rot: rot as Rot,
      })
    }
  }

  const seed = isFiniteInt(raw.seed) ? raw.seed >>> 0 : 1
  return {
    v: ROOM_VERSION,
    id: clampString(raw.id, 16, 'unknown'),
    name: clampString(raw.name, 24, '一间玩具屋'),
    ownerName: clampString(raw.ownerName, ROOM_CONFIG.maxNameLength, '匿名屋主'),
    taunt: clampString(raw.taunt, ROOM_CONFIG.maxTauntLength, ''),
    seed,
    width,
    depth,
    props,
    chest: { x: chestRaw.x, z: chestRaw.z },
    door: { x: doorRaw.x, z: doorRaw.z },
    createdAt: isFiniteInt(raw.createdAt) ? raw.createdAt : 0,
  }
}

/** Cells the raider cannot walk through, for reachability. */
export function blockedCells(room: RoomDef): Set<number> {
  const blocked = new Set<number>()
  for (const p of room.props) {
    if (propType(p.type).blocking) blocked.add(cellKey(p.x, p.z))
  }
  return blocked
}

/**
 * Breadth-first reachability over walkable cells. Traps that *hurt* are not
 * obstacles — being launched across the room is content, not a dead end. Only
 * genuinely impassable props (walls, holes, machines) block the path.
 */
export function reachableFrom(room: RoomDef, from: { x: number; z: number }): Set<number> {
  const blocked = blockedCells(room)
  const seen = new Set<number>()
  const start = cellKey(from.x, from.z)
  if (blocked.has(start)) return seen
  seen.add(start)
  const queue: Array<{ x: number; z: number }> = [{ x: from.x, z: from.z }]
  const steps = [
    { dx: 1, dz: 0 },
    { dx: -1, dz: 0 },
    { dx: 0, dz: 1 },
    { dx: 0, dz: -1 },
  ]
  while (queue.length > 0) {
    const cur = queue.shift()!
    for (const s of steps) {
      const nx = cur.x + s.dx
      const nz = cur.z + s.dz
      if (nx < 0 || nx >= room.width || nz < 0 || nz >= room.depth) continue
      const key = cellKey(nx, nz)
      if (seen.has(key) || blocked.has(key)) continue
      seen.add(key)
      queue.push({ x: nx, z: nz })
    }
  }
  return seen
}

export function isSolvable(room: RoomDef): boolean {
  return reachableFrom(room, room.door).has(cellKey(room.chest.x, room.chest.z))
}

export function checkRoom(room: RoomDef): CheckResult {
  const errors: Issue[] = []
  const warnings: Issue[] = []
  const spent = spentBudget(room.props)

  if (room.props.length > ROOM_CONFIG.maxProps) {
    errors.push({
      code: 'too-many-props',
      message: `玩具太多了（${room.props.length}/${ROOM_CONFIG.maxProps}）。`,
    })
  }

  if (spent > ROOM_CONFIG.budget) {
    errors.push({
      code: 'over-budget',
      message: `超出预算 ${spent - ROOM_CONFIG.budget} 点。拆掉一些，或者换便宜的骗法。`,
    })
  }

  const counts = countByType(room.props)
  for (const [type, n] of counts) {
    const t = propType(type)
    if (n > t.maxPerRoom) {
      errors.push({
        code: 'type-cap',
        message: `${t.name} 最多放 ${t.maxPerRoom} 个（现在 ${n} 个）。`,
      })
    }
  }

  const occupied = new Map<number, PropInstance>()
  for (const p of room.props) {
    if (p.x < 0 || p.x >= room.width || p.z < 0 || p.z >= room.depth) {
      errors.push({ code: 'out-of-bounds', message: '有玩具放到了房间外面。', iid: p.iid })
      continue
    }
    const key = cellKey(p.x, p.z)
    const prev = occupied.get(key)
    if (prev) {
      errors.push({ code: 'overlap', message: '两个玩具叠在同一格。', iid: p.iid })
      continue
    }
    occupied.set(key, p)
  }

  if (occupied.has(cellKey(room.door.x, room.door.z))) {
    errors.push({ code: 'door-blocked', message: '门口被玩具占住了。' })
  }
  if (occupied.has(cellKey(room.chest.x, room.chest.z))) {
    errors.push({ code: 'chest-blocked', message: '宝箱和别的玩具重叠了。' })
  }
  if (sameCell(room.door, room.chest)) {
    errors.push({ code: 'chest-at-door', message: '宝箱不能直接放在门口。' })
  }

  // Nobody may be trapped the moment they walk in.
  const doorDir = room.door.z === 0 ? 1 : -1
  for (let i = 1; i <= ROOM_CONFIG.doorClearance; i++) {
    const z = room.door.z + doorDir * i
    if (z < 0 || z >= room.depth) break
    if (occupied.has(cellKey(room.door.x, z))) {
      errors.push({
        code: 'door-clearance',
        message: `门口前 ${ROOM_CONFIG.doorClearance} 格要留空，不然他连进门的机会都没有。`,
      })
      break
    }
  }

  const dist = cellDistance(room.door, room.chest)
  if (dist < ROOM_CONFIG.minChestDistance) {
    errors.push({
      code: 'chest-too-close',
      message: `宝箱离门至少 ${ROOM_CONFIG.minChestDistance} 格，太近就没有戏。`,
    })
  }

  if (errors.length === 0 && !isSolvable(room)) {
    errors.push({
      code: 'unsolvable',
      message: '没有任何一条路能走到宝箱。挡死的屋子不会有人转发。',
    })
  }

  // Design nudges — allowed, but the builder should hear them.
  const trapCount = room.props.filter((p) => propType(p.type).behavior.type !== 'none').length
  if (trapCount === 0) {
    warnings.push({ code: 'no-traps', message: '一个机关都没有——他会走得很顺，也不会记得你。' })
  }
  if ((counts.get('bluff') ?? 0) === 0 && trapCount > 0) {
    warnings.push({
      code: 'no-bluff',
      message: '全是真机关。加一块假陷阱板，他才会开始怀疑自己。',
    })
  }
  if (room.taunt.trim().length === 0) {
    warnings.push({ code: 'no-taunt', message: '留一句话。挑衅是最便宜的机关。' })
  }
  if (spent < ROOM_CONFIG.budget / 3) {
    warnings.push({ code: 'underspent', message: '预算还剩很多，屋子会显得空。' })
  }

  return { ok: errors.length === 0, errors, warnings, spent, budget: ROOM_CONFIG.budget }
}

/** Can this single prop legally go at this cell right now? Used by the builder for live feedback. */
export function canPlace(
  room: RoomDef,
  type: Parameters<typeof propType>[0],
  x: number,
  z: number,
): { ok: boolean; reason?: string } {
  if (x < 0 || x >= room.width || z < 0 || z >= room.depth) {
    return { ok: false, reason: '超出房间' }
  }
  const key = cellKey(x, z)
  if (room.props.some((p) => cellKey(p.x, p.z) === key)) return { ok: false, reason: '这格有东西了' }
  if (key === cellKey(room.door.x, room.door.z)) return { ok: false, reason: '这是门口' }
  if (key === cellKey(room.chest.x, room.chest.z)) return { ok: false, reason: '这是宝箱' }

  const doorDir = room.door.z === 0 ? 1 : -1
  for (let i = 1; i <= ROOM_CONFIG.doorClearance; i++) {
    if (x === room.door.x && z === room.door.z + doorDir * i) {
      return { ok: false, reason: '门口要留空' }
    }
  }

  const t = propType(type)
  const counts = countByType(room.props)
  if ((counts.get(t.id) ?? 0) >= t.maxPerRoom) {
    return { ok: false, reason: `${t.name} 已达上限` }
  }
  if (spentBudget(room.props) + t.cost > ROOM_CONFIG.budget) {
    return { ok: false, reason: '预算不够' }
  }
  // Only walls/machines/holes can strand the chest, so only they pay for a BFS.
  // 12×12 makes this cheap enough to run on every hover.
  if (t.blocking) {
    const probe: RoomDef = {
      ...room,
      props: [...room.props, { iid: -1, type: t.id, x, z, rot: 0 }],
    }
    if (!isSolvable(probe)) return { ok: false, reason: '这样就完全挡死了' }
  }
  return { ok: true }
}
